import { StateCreator } from 'zustand';
import { getCurrentUser, API_BASE_URL } from '../../services/api';
import type { HnpUploadTab, UserType } from '../../services/api/types.gen';
import { setUserContext, addBreadcrumb } from '../../utils/sentry';
import {
  PermissionSlug,
  type FeatureFlagType,
} from '../../constants/featureFlags';
import type { ProductName } from '../../constants/products';
import { getRogerDocFeatureFlags } from '../../services/rogerDocApi';
import { events } from '../../services/events';
import { chatSessionReset, type ChatSlice } from './chatSlice';
import { CHAT_WINDOWS_STORAGE_KEY } from './chatWindows';

/** Align with backend WorkOS session cache TTL (~2 min). */
export const AUTH_ME_REFRESH_INTERVAL_MS = 2 * 60 * 1000;

type MeResponse = {
  id?: string;
  email?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  permissions?: string[];
  feature_flags?: string[];
  products?: Record<string, boolean>;
  active_clinic_ids?: string[];
  user_clinics?: UserClinicRole[];
  user_types?: UserType[];
  is_roger_employee?: boolean;
  agency?: User['agency'];
};

function userFromMeResponse(userResponse: MeResponse): User {
  return {
    id: userResponse.id || '',
    email: userResponse.email || '',
    name:
      userResponse.name ||
      `${userResponse.first_name || ''} ${userResponse.last_name || ''}`.trim(),
    role: userResponse.role || 'clinician',
    permissions: userResponse.permissions || [],
    feature_flags: userResponse.feature_flags || [],
    products: userResponse.products,
    active_clinic_ids: userResponse.active_clinic_ids,
    user_clinics: (userResponse.user_clinics || []).map(clinic => ({
      ...clinic,
      permissions: clinic.permissions || [],
    })),
    user_types: userResponse.user_types || [],
    is_roger_employee: userResponse.is_roger_employee ?? false,
    agency: userResponse.agency,
  };
}

function reloadForOrganizationChange(
  previousAgencyId: string,
  currentAgencyId: string
): void {
  addBreadcrumb('Organization changed in another tab; reloading', 'auth', {
    previousAgencyId,
    currentAgencyId,
  });
  console.info(
    '[auth] Detected organization change; clearing local state and reloading.',
    { previous: previousAgencyId, current: currentAgencyId }
  );
  localStorage.removeItem('roger-emr-store');
  window.location.reload();
}

export interface UserClinicRole {
  clinic_id: string;
  /** Permission slugs granted through clinic-scoped roles at this clinic. */
  permissions: string[];
  is_assignable_clinician: boolean;
  is_case_manager: boolean;
  is_approver: boolean;
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  permissions: string[];
  feature_flags: string[];
  /**
   * Products the current agency has, keyed by name. Absent means nobody has
   * said yet -- a store persisted before this shipped, or a hand-built test
   * fixture -- which the selectors read as "has it", mirroring the backend.
   * `userFromMeResponse` always sets it.
   */
  products?: Record<string, boolean>;
  /** Live UserClinic memberships at the current agency. */
  active_clinic_ids?: string[];
  user_clinics: UserClinicRole[];
  /**
   * Clinical titles this user holds at the current agency. Optional so the
   * many hand-built `User` fixtures in tests stay valid; `userFromMeResponse`
   * always sets it.
   */
  user_types?: UserType[];
  /** A member of the Roger team. Optional so hand-built test fixtures stay valid. */
  is_roger_employee?: boolean;
  agency?: {
    id: string;
    system_name: string;
    written_name: string;
    abbreviation: string;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    zip_code?: string | null;
    phone_number?: string | null;
    hnp_upload_tab?: HnpUploadTab | null;
    hnp_upload_task?: string | null;
    is_kinnser_enabled?: boolean | null;
    is_rogeremr_enabled?: boolean | null;
    timezone?: string;
  };
}

export interface AuthSlice {
  user: User | null;
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  authChecked: boolean;
  rogerDocFeatureFlags: Record<string, boolean>;
  loginWithWorkOS: () => Promise<void>;
  logout: () => void;
  setAuthLoading: (loading: boolean) => void;
  checkAuthStatus: () => Promise<void>;
  checkDevAutoLogin: () => Promise<boolean>;
  /** Refetch /auth/me to sync feature flags and detect org switches (no loading UI). */
  refreshCurrentUser: () => Promise<void>;
  /**
   * Force an immediate WorkOS token refresh, then update the store with the
   * latest feature flags. Shows no loading UI. Returns true on success.
   */
  forceRefreshWorkosSession: () => Promise<boolean>;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasFeatureFlag: (
    featureFlag: FeatureFlagType,
    source?: 'workos' | 'rogerdoc'
  ) => boolean;
  hasProduct: (product: ProductName) => boolean;
  hasAnyProduct: (products: readonly ProductName[]) => boolean;
  isKinnserEnabled: () => boolean;
}

export const selectIsRogerEmrEnabled = (
  state: Pick<AuthSlice, 'user'>
): boolean => state.user?.agency?.is_rogeremr_enabled === true;

type AuthSliceSet = Parameters<StateCreator<AuthSlice>>[0];
type AuthSliceGet = Parameters<StateCreator<AuthSlice>>[1];

/**
 * Load Docs feature flags into the store without blocking. On failure the
 * previous values stay; on first load that means WorkOS flags only.
 */
/**
 * Chat windows live in memory and sessionStorage, which outlast the cookie
 * logout: drop both so the next person on this tab does not inherit them.
 */
function clearChatSession(set: AuthSliceSet): void {
  // The combined store holds the chat slice; this slice only types its own part.
  (set as unknown as (state: Partial<ChatSlice>) => void)(chatSessionReset());
  try {
    sessionStorage.removeItem(CHAT_WINDOWS_STORAGE_KEY);
  } catch {
    // Blocked storage: nothing was saved.
  }
}

function loadRogerDocFeatureFlags(set: AuthSliceSet): void {
  getRogerDocFeatureFlags()
    .then(flags => set({ rogerDocFeatureFlags: flags }))
    .catch(() => {});
}

/**
 * Refetch /auth/me and sync the store. When `forceRefresh` is true the backend
 * is asked to refresh the WorkOS token first (to pick up updated feature flags);
 * otherwise the cached session is used. Detects org switches and reloads the
 * app rather than rendering one org's data under another org's session.
 * Returns true when the fetch succeeded (including the org-switch reload path).
 */
async function syncCurrentUserFromServer(
  set: AuthSliceSet,
  get: AuthSliceGet,
  forceRefresh: boolean
): Promise<boolean> {
  if (!get().isAuthenticated) {
    return false;
  }

  const currentAgencyId = get().user?.agency?.id;

  try {
    const userData = await getCurrentUser(
      forceRefresh ? { query: { force_refresh: true } } : {}
    );
    if (!userData?.data) {
      return false;
    }

    const userResponse = userData.data as MeResponse;
    const fetchedAgencyId = userResponse.agency?.id;

    if (
      currentAgencyId &&
      fetchedAgencyId &&
      fetchedAgencyId !== currentAgencyId
    ) {
      reloadForOrganizationChange(currentAgencyId, fetchedAgencyId);
      return true;
    }

    const user = userFromMeResponse(userResponse);
    set({ user, isAuthenticated: true });
    loadRogerDocFeatureFlags(set);
    return true;
  } catch (error) {
    console.error(
      forceRefresh
        ? 'Failed to force-refresh WorkOS session:'
        : 'Failed to refresh current user:',
      error
    );
    return false;
  }
}

export const authSlice: StateCreator<AuthSlice> = (set, get) => ({
  user: null,
  isAuthenticated: false,
  isAuthLoading: false,
  authChecked: false,
  rogerDocFeatureFlags: {},

  setAuthLoading: (loading: boolean) => {
    set({ isAuthLoading: loading });
  },

  checkDevAutoLogin: async (): Promise<boolean> => {
    // Check for dev auto-login when user is not authenticated
    if (import.meta.env.DEV && import.meta.env.VITE_DEV_AUTO_LOGIN === 'true') {
      // Use configured redirect path, or default to current path
      let redirectPath = import.meta.env.VITE_DEV_AUTO_LOGIN_REDIRECT;

      if (!redirectPath) {
        // Get current path for redirect after login
        const currentPath = window.location.pathname + window.location.search;
        // Default to a universally reachable page for root or login redirects.
        redirectPath =
          currentPath === '/' || currentPath === '/login'
            ? '/resources'
            : currentPath;
      }

      // Use fetch to authenticate via proxy (cookie gets set with localhost domain)
      // Then manually redirect to the target path
      try {
        // Optional: pick a named user from `DEV_AUTO_LOGIN_USERS_JSON` (e.g.
        // "clinician" or "case_manager"). Useful when running the local dev
        // frontend against a backend that has multi-user auto-login set up
        // and no legacy default credentials.
        const userKey = import.meta.env.VITE_DEV_AUTO_LOGIN_USER as
          string | undefined;
        const userParam = userKey ? `&user=${encodeURIComponent(userKey)}` : '';
        const response = await fetch(
          `${API_BASE_URL}/dev/auto-login?redirect_to=${encodeURIComponent(redirectPath)}${userParam}`,
          {
            credentials: 'include',
          }
        );
        const data = await response.json();

        if (data.success) {
          // Cookie is now set, redirect to the target path
          window.location.href = data.redirect_url;
        } else {
          console.error('Dev auto-login failed:', data);
        }
      } catch (error) {
        console.error('Dev auto-login fetch error:', error);
      }

      return true; // Indicate that we initiated auto-login
    }
    return false; // No auto-login performed
  },

  loginWithWorkOS: async (): Promise<void> => {
    set({ isAuthLoading: true });

    try {
      window.location.href = `${API_BASE_URL}/auth/login`;
    } catch (error) {
      console.error('WorkOS login error:', error);
      set({ isAuthLoading: false });
    }
  },

  logout: (): void => {
    try {
      events.stop();
      addBreadcrumb('User logout initiated', 'auth');

      // Clear persisted state from localStorage before logout
      localStorage.removeItem('roger-emr-store');
      clearChatSession(set);

      // Redirect to logout endpoint - this allows the backend
      // to handle the WorkOS logout properly by disabling the JWT cookie
      window.location.href = `${API_BASE_URL}/auth/logout`;
    } catch (error) {
      console.error('Logout error:', error);
      addBreadcrumb('User logout error', 'auth', {
        error: error instanceof Error ? error.message : String(error),
      });

      // Even if logout fails, clear local state and persisted state
      localStorage.removeItem('roger-emr-store');
      clearChatSession(set);
      set({
        user: null,
        isAuthenticated: false,
        authChecked: true,
        rogerDocFeatureFlags: {},
      });
    }
  },

  checkAuthStatus: async (): Promise<void> => {
    try {
      set({ isAuthLoading: true });

      const userData = await getCurrentUser({});

      if (userData && userData.data) {
        const user = userFromMeResponse(userData.data as MeResponse);

        // Log user permissions for debugging
        console.log('User Authenticated:', {
          email: user.email,
          name: user.name,
          role: user.role,
          permissions: user.permissions,
          feature_flags: user.feature_flags || [],
          agency: user.agency,
        });

        // Set Sentry user context
        setUserContext({
          id: user.id,
          email: user.email,
          username: user.name,
        });

        addBreadcrumb('Auth status check successful', 'auth', {
          userId: user.id,
        });

        set({
          user,
          isAuthenticated: true,
          isAuthLoading: false,
          authChecked: true,
        });

        loadRogerDocFeatureFlags(set);
      } else {
        // Check for dev auto-login before marking as unauthenticated
        const didRedirect = await get().checkDevAutoLogin();

        if (!didRedirect) {
          set({
            user: null,
            isAuthenticated: false,
            isAuthLoading: false,
            authChecked: true,
          });
        }
      }
    } catch (error) {
      console.error('Auth status check error:', error);
      addBreadcrumb('Auth status check error', 'auth', {
        error: error instanceof Error ? error.message : String(error),
      });

      // Check for dev auto-login before marking as unauthenticated
      const didRedirect = await get().checkDevAutoLogin();

      if (!didRedirect) {
        set({
          user: null,
          isAuthenticated: false,
          isAuthLoading: false,
          authChecked: true,
        });
      }
    }
  },

  refreshCurrentUser: async (): Promise<void> => {
    await syncCurrentUserFromServer(set, get, false);
  },

  forceRefreshWorkosSession: async (): Promise<boolean> => {
    return syncCurrentUserFromServer(set, get, true);
  },

  hasPermission: (permission: string) => {
    const userPermissions = get().user?.permissions || [];
    if (userPermissions.includes(PermissionSlug.ADMIN_SUPER_USER)) {
      return true;
    }
    return userPermissions.includes(permission);
  },

  hasAnyPermission: (permissions: string[]) => {
    const userPermissions = get().user?.permissions || [];
    if (userPermissions.includes(PermissionSlug.ADMIN_SUPER_USER)) {
      return true;
    }
    return permissions.some(permission => userPermissions.includes(permission));
  },

  hasFeatureFlag: (
    featureFlag: FeatureFlagType,
    source?: 'workos' | 'rogerdoc'
  ) => {
    const checkWorkOS = () =>
      (get().user?.feature_flags || []).includes(featureFlag);
    const checkRogerDoc = () => {
      const normalize = (s: string) => s.replace(/[-_]/g, '_');
      const searchKey = normalize(featureFlag);
      return Object.entries(get().rogerDocFeatureFlags).some(
        ([k, v]) => v && normalize(k) === searchKey
      );
    };
    if (source === 'workos') return checkWorkOS();
    if (source === 'rogerdoc') return checkRogerDoc();
    return checkWorkOS() || checkRogerDoc();
  },

  /**
   * True when the agency has this product.
   *
   * No map at all means nobody has said yet, so answer true. A store persisted
   * before this shipped has no `products` until the next `/auth/me`, and
   * reading that as "off" would blank every gated surface for a page load.
   * The backend answers the same way for an agency Docs has not told it about.
   */
  hasProduct: (product: ProductName) => {
    const products = get().user?.products;
    return products === undefined || products[product] === true;
  },

  /**
   * True when the agency has at least one of these products.
   *
   * Mirrors `hasAnyPermission`. For a surface that more than one product
   * unlocks.
   */
  hasAnyProduct: (products: readonly ProductName[]) =>
    products.some(product => get().hasProduct(product)),

  isKinnserEnabled: () => get().user?.agency?.is_kinnser_enabled === true,
});
